import { inject, Injectable, Injector } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { combineLatest, forkJoin, Observable } from 'rxjs';
import { Store } from '@ngrx/store';
import { BreadCrumb } from '../models/common.models';
import { ILocalizedStoreAddress, INavigationNodes, INavigationResponse, IStore, IStoreAddress } from '../models/settings.model';
import {
  BreadcrumbsService,
  FooterService,
  I18nService,
  NavigationBarService,
  ProductListService,
  ProductsService,
} from '../services';
import { State } from '../reducers';
import { CustomerFacade } from './customer.facade';
import * as AppSelectors from '../reducers/app.reducer';
import * as AppActions from '../actions/app.actions';
import { ESeoData, ETitleSources } from '../configurations/seo';
import {
  ECheckoutFlows,
  EFeatureToggles,
  EFullPageLoaderListOptions,
  ERouteDataTypes,
  EStoreFeatures,
  ETermsAndConsentsType,
  EUserRoles,
  FEATURE_CONFIG,
  FEATURE_TOGGLE,
} from '../configurations/common';
import { AppUtils } from '../utils/app.utils';
import { TermsUtils } from '../utils/terms.utils';
import { environment } from '../../environments/environment';
import { IFooter } from '../models/footer.models';
import { HomepageActions } from '../actions';
import {
  selectShopCategoryIsAnyTileActive,
  selectShopCategoryLoadingError,
  selectShopCategoryOptions,
  selectShopCategoryOptionsLoading,
} from '../reducers/homepage.reducer';
import { INotification, IIndexedNotification } from '../models/cpq.models';
import { map, switchMap, take } from 'rxjs/operators';
import { IBaseConfig } from '../models/enviroment-delivery-details.model';

@Injectable({
  providedIn: 'root',
})
export class ConfigurationFacade {
  private readonly injector: Injector = inject(Injector);
  private readonly store: Store<State> = inject(Store<State>);
  private readonly customerFacade: CustomerFacade = inject(CustomerFacade);
  private pNavigationBarService: NavigationBarService | undefined;
  private pFooterService: FooterService | undefined;
  private pBreadcrumbsService: BreadcrumbsService | undefined;
  private pProductService: ProductsService | undefined;
  private pProductListService: ProductListService | undefined;
  private pI18nService: I18nService | undefined;

  // Roles are sorted by priority from highest to lowest.
  private readonly rolesByPriority: string[] = [
    'business_partner_approver',
    'business_partner_buyer',
    'approver',
    'buyer',
  ];

  /**
   * Map user role to translation key suffix.
   * @param {string} userRole
   * @returns {string}
   * @private
   */
  private mapUserRoleToTranslationKey(userRole: string): string {
    switch (userRole) {
      case EUserRoles.BusinessPartnerApprover:
        return 'business_partner_approver';
      case EUserRoles.BusinessPartnerBuyer:
        return 'business_partner_buyer';
      case EUserRoles.Approver:
        return 'approver';
      case EUserRoles.Buyer:
        return 'buyer';
      default:
        return '';
    }
  }

  /**
   * Get translation key based on user role priority.
   * Example:
   * - baseKey = 'regular_flow_details_1_page_visibility_of_inputs'
   * - user has following roles: ['RIGHT_WEBSHOPS_ORDER_USER', 'RIGHT_WEBSHOPS_APPROVER_USER']
   * - return 'regular_flow_details_1_page_visibility_of_inputs_approver'
   * @param {string} baseKey
   * @returns {Observable<string>}
   * @private
   */
  private _getRoleBasedTranslationKey(baseKey: string): Observable<string> {
    return this.customerFacade.selectCustomerCompanyRoles().pipe(
      take(1),
      map((userRoles: EUserRoles[]) => {
        const mappedRoles: Set<string> = new Set(userRoles.map((role: EUserRoles) => this.mapUserRoleToTranslationKey(role)));
        const matchedRole: string | undefined = this.rolesByPriority.find((role: string) => mappedRoles.has(role));
        return matchedRole ? `${baseKey}_${matchedRole}` : baseKey;
      }),
    );
  }

  /**
   * Get configuration based on user role. If there is not configuration for specified role, then use default key.
   * Example:
   * - baseKey = 'regular_flow_details_1_page_visibility_of_inputs'
   * - key based on user role = 'regular_flow_details_1_page_visibility_of_inputs_approver'
   * - there is not configuration for 'regular_flow_details_1_page_visibility_of_inputs_approver'
   * - use default key 'regular_flow_details_1_page_visibility_of_inputs'
   * @param {string} baseKey
   * @returns {Observable<string[]>}
   * @private
   */
  private _getRoleBasedConfigArray(baseKey: string): Observable<string[]> {
    return this._getRoleBasedTranslationKey(baseKey).pipe(
      take(1),
      switchMap((keyToCheck: string) =>
        forkJoin([
          this.i18nService.getTranslationByKey(keyToCheck).pipe(take(1)),
          this.i18nService.getTranslationByKey(baseKey).pipe(take(1)),
        ]).pipe(
          map(([roleData, defaultData]: [string, string]) => {
              const isValidRoleData: string | boolean = roleData && roleData !== keyToCheck;
              return this._parseStringToArray(isValidRoleData ? roleData : defaultData);
            },
          ),
        ),
      ),
    );
  }

  public get navigationBarService(): NavigationBarService {
    if (!this.pNavigationBarService) {
      this.pNavigationBarService = this.injector.get(NavigationBarService);
    }
    return this.pNavigationBarService;
  }

  public get productListService(): ProductListService {
    if (!this.pProductListService) {
      this.pProductListService = this.injector.get(ProductListService);
    }
    return this.pProductListService;
  }

  public get footerService(): FooterService {
    if (!this.pFooterService) {
      this.pFooterService = this.injector.get<FooterService>(FooterService);
    }
    return this.pFooterService;
  }

  public get breadcrumbsService(): BreadcrumbsService {
    if (!this.pBreadcrumbsService) {
      this.pBreadcrumbsService = this.injector.get<BreadcrumbsService>(BreadcrumbsService);
    }
    return this.pBreadcrumbsService;
  }

  public get productService(): ProductsService {
    if (!this.pProductService) {
      this.pProductService = this.injector.get(ProductsService);
    }
    return this.pProductService;
  }

  public get i18nService(): I18nService {
    if (!this.pI18nService) {
      this.pI18nService = this.injector.get(I18nService);
    }
    return this.pI18nService;
  }

  getNavigationBar(): Observable<INavigationResponse> {
    return this.navigationBarService.getNavigationBar();
  }

  getShopCategoryTiles(): Observable<INavigationResponse> {
    return this.navigationBarService.getShopCategoryNavigationBar();
  }

  loadShopCategoryTiles() {
    this.store.dispatch(HomepageActions.loadShopCategoryNavigation());
  }

  selectShopCategoryTiles(): Observable<INavigationNodes[]> {
    return this.store.select(selectShopCategoryOptions);
  }

  selectShopCategoryTilesLoading(): Observable<boolean> {
    return this.store.select(selectShopCategoryOptionsLoading);
  }

  selectShopCategoryTilesError(): Observable<string> {
    return this.store.select(selectShopCategoryLoadingError);
  }

  selectShopCategoryIsAnyTileActive(): Observable<boolean> {
    return this.store.select(selectShopCategoryIsAnyTileActive);
  }

  getFooterNavigation(): Observable<IFooter> {
    return this.footerService.getFooterNav();
  }

  getSocialMediaFooter(): Observable<IFooter> {
    return this.footerService.getSocialMediaFooter();
  }

  getBreadcrumbs(route: ActivatedRoute): BreadCrumb[] {
    return this.breadcrumbsService.getBreadcrumbs(route);
  }

  getBreadcrumbsParam(route: ActivatedRoute, param: ESeoData | ERouteDataTypes): ETitleSources | boolean {
    return this.breadcrumbsService.getBreadcrumbsParam(route, param);
  }

  getProductWithCategoryTree(sku: string): Promise<any> {
    return this.productService.getProductWithCategoryTree(sku);
  }

  getDefaultLanguage(): string {
    return this.i18nService.getDefaultLanguage();
  }

  validateMarket(market: string): boolean {
    return this.i18nService.validateMarket(market);
  }

  validateLanguage(language: string): boolean {
    return this.i18nService.validateLanguage(language);
  }

  getLangParamData(langParam: string): string[] {
    return this.i18nService.getLangParamData(langParam);
  }

  buildLangParam(market: string, language: string): string {
    return this.i18nService.buildLangParam(market, language);
  }

  getCurrentLanguage(): string {
    return this.i18nService.getCurrentLanguage();
  }

  getCurrentParam(): string {
    return this.i18nService.getCurrentParam();
  }

  getLanguageRegex(url: boolean = false): RegExp {
    if (url) {
      return I18nService.languageUrlRegex;
    }
    return I18nService.languageParamRegex;
  }

  getTranslationByKey(key: string | Array<string>, params?: any): Observable<any> {
    return this.i18nService.getTranslationByKey(key, params);
  }

  getCurrentStoreAddress(): IStoreAddress | undefined {
    const address: ILocalizedStoreAddress | undefined = AppUtils.getCurrentStore().address;
    return address?.[this.getCurrentLanguage()] ?? address?.['default'];
  }

  setAlert(alert: any): void {
    this.store.dispatch(AppActions.setAlert({alert}));
  }

  clearAlert(): void {
    this.store.dispatch(AppActions.clearAlert());
  }

  getAlert(): Observable<any> {
    return this.store.select(AppSelectors.getAlert);
  }

  getTermsUrl(termsKey: string, lang?: string): any {
    if (!lang) {
      lang = this.getCurrentLanguage();
    }
    const store: IStore = AppUtils.getCurrentStore();
    return TermsUtils.getTermsUrl(termsKey, lang, store);
  }

  /**
   * Return true if store feature is available for current store within the built environment,
   * otherwise returns false.
   *
   * @param {EStoreFeatures} feature
   * @returns {boolean}
   */
  isFeatureAvailable(feature: EStoreFeatures): boolean {
    const currentStoreId: string = AppUtils.getCurrentStore().marketCode;
    const features: Record<EStoreFeatures, string[]> = environment.features;
    return features[feature].includes(currentStoreId.toUpperCase());
  }

  /**
   * Return true if store feature is enabled via toggle,
   * otherwise return false
   *
   * @param {EFeatureToggles} featureToggle
   * @returns {Observable<boolean>}
   */
  isFeatureEnabled(featureToggle: EFeatureToggles): Observable<boolean> {
    const featureToggleKey: string = FEATURE_TOGGLE + featureToggle;
    return this.i18nService.getTranslationByKey(featureToggleKey).pipe(
      map(translationValue => translationValue.toString().toLowerCase() === 'true'),
    );
  }

  /**
   * Get configuration value by key. If there is only one value, return string, otherwise return array of strings.
   * @param {string} key
   * @returns {Observable<string[] | string>}
   */
  getConfigValue(key: string): Observable<string[] | string> {
    return this.i18nService.getTranslationByKey(`${FEATURE_CONFIG}${key}`)
      .pipe(
        map((data: string) => {
          if (data.includes(',')) {
            return this._parseStringToArray(data);
          }
          return data;
        }),
      );
  }

  /**
   * Returns SAP integration status
   * @returns {Observable<{sapIntegration: boolean, sapIntegrationRequired: boolean}>}
   */
  getSapStatus(): Observable<{sapIntegration: boolean; sapIntegrationRequired: boolean}> {
    return combineLatest([
      this.isFeatureEnabled(EFeatureToggles.SAP_INTEGRATION),
      this.isFeatureEnabled(EFeatureToggles.SAP_INTEGRATION_REQUIRED),
    ]).pipe(
      map(([sapIntegration, sapIntegrationRequired]) => ({
        sapIntegration,
        sapIntegrationRequired,
      })),
    );
  }

  /**
   * Returns true if the price disputing feature is active for the current user role.
   *
   * @returns {Observable<boolean>}
   */
  isPriceDisputingActiveForCurrentRole(): Observable<boolean> {
    return this.i18nService.getTranslationByKey(`${FEATURE_CONFIG}price_disputing_active_for_roles`)
      .pipe(
        switchMap((data: string) => {
          const allowedRoles: string[] = this._parseStringToArray(data);
          return this.customerFacade.selectCustomerCompanyRoles().pipe(
            map((userRoles: EUserRoles[]) => {
              return allowedRoles.some((allowedRole: string) => userRoles.includes(allowedRole as EUserRoles));
            }),
          );
        }),
      );
  }

  /**
   * Returns true of all feature toggle values are true,
   * otherwise return false
   *
   * @param {EFeatureToggles[]} featureToggles
   * @returns {Observable<boolean>}
   */
  areAllFeaturesEnabled(featureToggles: EFeatureToggles[]): Observable<boolean> {
    const featureToggleKeys: string[] = featureToggles.map((featureToggle: EFeatureToggles) => FEATURE_TOGGLE + featureToggle);
    return this.i18nService.getTranslationByKey(featureToggleKeys).pipe(
      map((translationValues: Record<string, string>) => Object.values(translationValues)
        .every((translationValue: string) => translationValue?.toString()?.toLowerCase() === 'true')),
    );
  }

  /**
   * Return array of strings separated with comma.
   * Prefix can be FEATURE_TOGGLE, FEATURE_CONFIG
   * Value is the key of the translation.
   *
   * @param {string} prefix
   * @param {string} value
   * @returns {Array<string>}
   */
  getArrayValues(prefix: string, value: string): Observable<Array<string>> {
    return this.i18nService.getTranslationByKey(prefix + value)
      .pipe(
        map(data => data.split(',')),
      );
  }

  /**
   * Get dynamic form configuration for delivery details page. (Visible inputs on the current delivery detail page).
   *
   * @param {ECheckoutFlows} detailsPageType
   * @param {number} detailsPageNumber
   * @returns {Observable<string[]>}
   */
  getDynamicDeliveryDetailsFormConfiguration(detailsPageType: ECheckoutFlows, detailsPageNumber: number): Observable<string[]> {
    const baseKey: string = `${FEATURE_CONFIG}${detailsPageType}_details_${detailsPageNumber}_page_visibility_of_inputs`;
    return this._getRoleBasedConfigArray(baseKey);
  }

  /**
   * Get dynamic form configuration for delivery details page. (Required inputs).
   *
   * @param {ECheckoutFlows} detailsPageType
   * @returns {Observable<string[]>}
   */
  getDynamicDeliveryDetailsFormRequiredInputs(detailsPageType: ECheckoutFlows): Observable<string[]> {
    const baseKey: string = `${FEATURE_CONFIG}${detailsPageType}_details_page_required_inputs`;
    return this._getRoleBasedConfigArray(baseKey);
  }

  /**
   * Get dynamic form configuration for delivery details page. (Read only inputs).
   *
   * @param {ECheckoutFlows} detailsPageType
   * @returns {Observable<string[]>}
   */
  getDynamicDeliveryDetailsFormReadOnlyInputs(detailsPageType: ECheckoutFlows): Observable<string[]> {
    const baseKey: string = `${FEATURE_CONFIG}${detailsPageType}_details_page_read_only_inputs`;
    return this._getRoleBasedConfigArray(baseKey);
  }

  /**
   * Get dynamic form configuration for delivery details page. (Visible inputs for approver section).
   *
   * @returns {Observable<string[]>}
   */
  getDynamicDeliveryDetailsApproverFormConfiguration(): Observable<string[]> {
    const baseKey: string = `${FEATURE_CONFIG}delivery_details_page_approver_section.visibility_of_inputs`;
    return this._getRoleBasedConfigArray(baseKey);
  }

  /**
   * Get dynamic form configuration for delivery details page. (Required inputs for approver section).
   *
   * @returns {Observable<string[]>}
   */
  getDynamicDeliveryDetailsApproverFormRequiredInputs(): Observable<string[]> {
    const baseKey: string = `${FEATURE_CONFIG}delivery_details_page_approver_section.required_inputs`;
    return this._getRoleBasedConfigArray(baseKey);
  }

  /**
   * Get dynamic form configuration for delivery details page. (Read only inputs for approver section).
   *
   * @returns {Observable<string[]>}
   */
  getDynamicDeliveryDetailsApproverFormReadOnlyInputs(): Observable<string[]> {
    const baseKey: string = `${FEATURE_CONFIG}delivery_details_page_approver_section.read_only_inputs`;
    return this._getRoleBasedConfigArray(baseKey);
  }

  /**
   * Get dynamic form configuration for order review page. If there is no order summary section, then add it to the
   * end of the array.
   * @returns {Observable<string[]>}
   */
  getDynamicCheckoutSections(): Observable<string[]> {
    return this.i18nService.getTranslationByKey(`${FEATURE_CONFIG}checkout_sections`)
      .pipe(map((data: string) => {
        const sections: string[] = this._parseStringToArray(data);
        if (!sections.includes('order_summary')) {
          sections.push('order_summary');
        }
        return sections;
      }));
  }

  getDynamicCheckoutSectionsRequiredFields(): Observable<string[]> {
    return this.i18nService.getTranslationByKey(`${FEATURE_CONFIG}checkout_sections_required`)
      .pipe(map((data: string) => this._parseStringToArray(data)));
  }

  getDynamicCheckoutAddresses(): Observable<string[]> {
    return this.i18nService.getTranslationByKey(`${FEATURE_CONFIG}checkout_addresses`)
      .pipe(map((data: string) => this._parseStringToArray(data)));
  }

  getDynamicCheckoutPaymentMethods$(): Observable<string[]> {
    return this.i18nService.getTranslationByKey(`${FEATURE_CONFIG}checkout_payment_methods`)
      .pipe(map((data: string) => this._parseStringToArray(data)));
  }

  getDynamicCheckoutPaymentMethodDefault$(): Observable<string> {
    return this.i18nService.getTranslationByKey(`${FEATURE_CONFIG}checkout_payment_method_default`);
  }

  /**
   * Get equipment identification key for current store.
   * @returns {Observable<string>}
   */
  getEquipmentIdentification$(): Observable<string> {
    return this.i18nService.getTranslationByKey(`${FEATURE_CONFIG}equipment_identification`);
  }

  /**
   * Get dynamic terms and conditions for delivery details page for Generate quote flow.
   *
   * generate_quote_consent_checkboxes
   * checkout_consent_checkboxes
   * @returns {Observable<string[]>}
   */
  getDynamicConsentCheckboxes(consentsCheckboxesType: ETermsAndConsentsType): Observable<string[]> {
    return this.i18nService.getTranslationByKey(`${FEATURE_CONFIG}${consentsCheckboxesType}_consent_checkboxes`)
      .pipe(map((data: string) => this._parseStringToArray(data)));
  }

  /**
   * Get dynamic terms and conditions for delivery details page for Generate quote flow.
   *
   * generate_quote_consent_required_checkboxes
   * create_order_consent_required_checkboxes
   * @returns {Observable<string[]>}
   */
  getDynamicRequiredCheckboxes(consentsCheckboxesType: ETermsAndConsentsType): Observable<string[]> {
    return this.i18nService.getTranslationByKey(`${FEATURE_CONFIG}${consentsCheckboxesType}_consent_checkboxes_required`)
      .pipe(map((data: string) => this._parseStringToArray(data)));
  }

  /**
   * Get dynamic form departments options for delivery details page.
   *
   * @returns {Observable<IBaseConfig[]>}
   */
  getDynamicDeliveryDetailsFormDepartments(): Observable<IBaseConfig[]> {
    return this.i18nService.getTranslationByKey(
      `${FEATURE_CONFIG}delivery_details_page_departments_${this.i18nService.getCurrentLanguage()}`,
    ).pipe(
      map((data: string) => this._parseStringToArray(data)),
      map((array: string[]) => array.map((department: string) => ({
        name: department,
        value: department,
      }))),
    );
  }

  /**
   * Get dynamic form delivery times options for delivery details page.
   *
   * @returns {Observable<IBaseConfig[]>}
   */
  getDynamicDeliveryDetailsFormDeliveryTimes(): Observable<IBaseConfig[]> {
    return this.i18nService.getTranslationByKey(
      `${FEATURE_CONFIG}delivery_details_page_delivery_times_${this.i18nService.getCurrentLanguage()}`,
    ).pipe(
      map((data: string) => this._parseStringToArray(data)),
      map((array: string[]) => array.map((department: string) => ({
        name: department,
        value: department,
      }))),
    );
  }

  /**
   * Get dynamic fields for order thank you page.
   *
   * @returns {Observable<string[]>}
   */
  getDynamicOrderThankYouPageFields(): Observable<string[]> {
    return this.i18nService.getTranslationByKey(`${FEATURE_CONFIG}order_thank_you_page_fields`)
      .pipe(map((data: string) => this._parseStringToArray(data)));
  }

  /**
   * Get dynamic price fields for order thank you page.
   *
   * @returns {Observable<string[]>}
   */
  getDynamicOrderThankYouPagePriceFields(): Observable<string[]> {
    return this.i18nService.getTranslationByKey(`${FEATURE_CONFIG}order_thank_you_page_price_fields`)
      .pipe(map((data: string) => this._parseStringToArray(data)));
  }

  /**
   * Get tabs, which should be shown on purchase activity page.
   * @returns {Observable<string[]>}
   */
  getDynamicPurchaseActivityTabs(): Observable<string[]> {
    return this.i18nService.getTranslationByKey(`${FEATURE_CONFIG}purchase_activity_tabs`)
      .pipe(map((data: string) => this._parseStringToArray(data)));
  }

  /**
   * Get previous orders tabs, which should be shown on purchase activity page.
   * @returns {Observable<string[]>}
   */
  getDynamicPurchaseActivityPreviousOrdersTabs(): Observable<string[]> {
    return this.i18nService.getTranslationByKey(`${FEATURE_CONFIG}purchase_activity_previous_orders_tabs`)
      .pipe(map((data: string) => this._parseStringToArray(data)));
  }

  /**
   * Get dynamic fields for purchase activity details page.
   *
   * @param {string} resourceType
   * @returns {Observable<string[]>}
   */
  getDynamicPurchaseActivityDetailsFields(resourceType: string): Observable<string[]> {
    return this.i18nService.getTranslationByKey(`${FEATURE_CONFIG}purchase_activity_details_page_${resourceType}_fields`)
      .pipe(map((data: string) => this._parseStringToArray(data)));
  }

  /**
   * Get dynamic price fields for purchase activity details page.
   *
   * @param {string} resourceType
   * @returns {Observable<string[]>}
   */
  getDynamicPurchaseActivityDetailsPriceFields(resourceType: string): Observable<string[]> {
    return this.i18nService.getTranslationByKey(`${FEATURE_CONFIG}purchase_activity_details_page_${resourceType}_price_fields`)
      .pipe(map((data: string) => this._parseStringToArray(data)));
  }

  /**
   * Get dynamic item fields of order for purchase activity details page.
   *
   * @returns {Observable<string[]>}
   */
  getDynamicPurchaseActivityDetailsOrderItemFields(): Observable<string[]> {
    return this.i18nService.getTranslationByKey(`${FEATURE_CONFIG}purchase_activity_details_page_order_item_fields`)
      .pipe(map((data: string) => this._parseStringToArray(data)));
  }

  /**
   * Get tabs, which should be shown on my installed base page.
   * @returns {Observable<string[]>}
   */
  getDynamicMyInstalledBaseTabs(): Observable<string[]> {
    return this.i18nService.getTranslationByKey(`${FEATURE_CONFIG}my_installed_base_tabs`)
      .pipe(map((data: string) => this._parseStringToArray(data)));
  }

  /**
   * Get equipment filters for advanced search on my installed base.
   * @returns {Observable<string[]>}
   */
  getDynamicMyInstalledBaseEquipmentFilters(): Observable<string[]> {
    return this.i18nService.getTranslationByKey(`${FEATURE_CONFIG}my_installed_base_equipment_filters`)
      .pipe(map((data: string) => this._parseStringToArray(data)));
  }

  /**
   * Get file types which can be uploaded via Drag and Drop.
   *
   * @returns {Observable<string[]>}
   */
  getSupportedFileTypesForDragDropUpload(): Observable<string[]> {
    return this.i18nService.getTranslationByKey(`${FEATURE_CONFIG}checkout_file_upload_supported_file_types`)
      .pipe(map((data: string) => this._parseStringToArray(data)));
  }

  /**
   * Get interval for getting order SAP info.
   * @returns {Observable<number>}
   */
  public getPendingPageGettingOrdersInterval(): Observable<number> {
    return this.i18nService.getTranslationByKey(`${FEATURE_CONFIG}pending_page_get_order_interval`)
      .pipe(map(Number));
  }

  /**
   * Get supported product categories for non-equipment purchases.
   * @returns {Observable<string[]>}
   */
  getNonEquipmentPurchaseSupportedCategories$(): Observable<string[]> {
    return this.i18nService.getTranslationByKey(`${FEATURE_CONFIG}supported_product_categories_for_non_equipment_purchases`)
      .pipe(map((data: string) => this._parseStringToArray(data)));
  }

  /**
   * Get the validation regex for name fields.
   * Retrieves `names_fields_validation_regex` config value and converts it into a `RegExp` object.
   * @returns {Observable<RegExp>} An observable that emits a `RegExp` built from the configured pattern string.
   */
  getNamesFieldsValidationRegex$(): Observable<RegExp> {
    return this.i18nService.getTranslationByKey(`${FEATURE_CONFIG}names_fields_validation_regex`)
      .pipe(map((data: string) => new RegExp(data)));
  }

  /**
   * Split string data (configurations) to array of strings.
   *
   * @param {string} data
   * @returns {string[]}
   * @private
   */
  private _parseStringToArray(data: string): string[] {
    return data ? data.split(',') : [];
  }

  /**
   * setTimout function is used, because delay is needed so the notification is
   * not closed with the same click, which opened it
   *
   * setTimeout is used as a "zero-delay timeout/deferred function" technique
   *
   * @param {INotification} notification
   */
  appendNotification(notification: INotification): void {
    setTimeout(() => {
      this.store.dispatch(AppActions.appendNotification({notification}));
    });
  }

  closeNotification(notificationId: number) {
    this.store.dispatch(AppActions.closeNotification({notificationId}));
  }

  clearNotifications(): void {
    this.store.dispatch(AppActions.clearNotifications());
  }

  getNotification(): Observable<IIndexedNotification[]> {
    return this.store.select(AppSelectors.getNotification);
  }

  redirectToLastPage(): void {
    this.store.dispatch(AppActions.redirectToLastPage());
  }

  selectFullPageLoaderList(): Observable<string[]> {
    return this.store.select(AppSelectors.selectFullPageLoaderList);
  }

  /**
   * Push EFullPageLoaderListOptions.NAVIGATION name to the fullPageLoaderList
   */
  navBarLoaded(): void {
    this.store.dispatch(AppActions.navBarLoaded({name: EFullPageLoaderListOptions.NAVIGATION}));
  }

  /**
   * Push EFullPageLoaderListOptions.HOMEPAGE_BANNER name to the fullPageLoaderList
   */
  homePageBannerLoaded(): void {
    this.store.dispatch(AppActions.homePageBannerLoaded({name: EFullPageLoaderListOptions.HOMEPAGE_BANNER}));
  }

  /**
   * Get equipment filters for unharmonized installed base.
   * @returns {Observable<string[]>}
   */
  // TODO: Remove this functionality when harmonization will be done
  getUnharmonizedEquipmentFilters(): Observable<string[]>{
    return this.i18nService.getTranslationByKey(`${FEATURE_CONFIG}equipment_filters`)
      .pipe(map((data: string) => this._parseStringToArray(data)));
  }
}
